import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { createPanelTransport, findPanelRecord, serializePanelSave } from './panelSizingRequests';
import { selectApiUrls, selectUpdatedApis } from '../../redux/slices/apiUrlSlice';
import { selectUserDetails, selectUserRoles } from '../../redux/slices/userSlice';

function PanelSizingWIP({
  generalProjectData = [],
  electricalOptionsData = {},
  waterTreatmentData = {},
  generalStationData = {},
  fertigationData = {},
  pumpsData = {},
  panelSizingData = {},
  loading = false,
  error = null,
  showSaveNotificationMessage = null,
  onDataUpdate = null
}) {
  const dispatch = useDispatch();
  const transport = createPanelTransport(dispatch);
  const apiUrls = useSelector(selectApiUrls);
  const updatedApis = useSelector(selectUpdatedApis);
  const userDetails = useSelector(selectUserDetails);
  const userRoles = useSelector(selectUserRoles);
  const { options } = useSelector((state) => state.dropdown);

  const currentUser = userDetails?.name || userDetails?.displayName || 'Unknown';
  const primaryRole = userRoles?.length > 0 ? userRoles[0] : 'User';

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'JobNumber', direction: 'ascending' });
  const [copiedJobId, setCopiedJobId] = useState(null);
  const [copiedFolderJobId, setCopiedFolderJobId] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [editingFields, setEditingFields] = useState({});
  const [markingDone, setMarkingDone] = useState({});
  useRealtimeDraft(Object.values(editingFields).some(Boolean) || Object.values(savingFields).some(Boolean) || Object.values(markingDone).some(Boolean) || Object.keys(editedValues).length > 0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);

  const statusOptions =
    options?.filter((option) => option.DropdownType === 'Panel Review Status')
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  const actionOptions =
    options?.filter((option) =>
      option.DropdownType === 'Panel Review Action' ||
      option.DropdownType === 'Panel Sizing Action' ||
      option.DropdownType === 'Panel Action'
    ).sort((a, b) => a.SortOrder - b.SortOrder) || [];

  const isValueInDropdown = (value, dropdownOptions) =>
    dropdownOptions.some((option) => option.DisplayText === value || option.displayName === value);

  const combinedData = useMemo(() => {
    return generalProjectData.map((project) => {
      const projectId = project.ID;
      const elecOptions = electricalOptionsData[projectId] || {};
      const waterTreatment = waterTreatmentData[projectId] || {};
      const generalStation = generalStationData[projectId] || {};
      const fertigation = fertigationData[projectId] || {};
      const pumps = pumpsData[projectId] || {};
      const panelSizing = panelSizingData[projectId] || {};

      return {
        ID: project.ID,
        JobNumber: project.JobNumber,
        ProjectName: project.ProjectName,
        ModelNumber: project.ModelNumber,
        Zones: project.Zones,
        ElectricalEnclosureActualSize: elecOptions.ElectricalEnclosureActualSize,
        Sunshade: elecOptions.Sunshade,
        DeadFront: elecOptions.DeadFront,
        ElectricalOptions: elecOptions.ElectricalOptions,
        IndividualMotorDisconnects: elecOptions.IndividualMotorDisconnects,
        HasUV: waterTreatment.HasUV,
        PrimeSystem: generalStation.PrimeSystem,
        WatertronicsFertigation: fertigation.WatertronicsFertigation,
        HasTransfer: pumps.HasTransfer,
        HasWaterFeature: pumps.HasWaterFeature,
        Orientation: panelSizing.Orientation,
        Drives: panelSizing.Drives,
        Cooling: panelSizing.Cooling,
        HMI: panelSizing.HMI,
        DoorMods: panelSizing.DoorMods,
        ModStd: panelSizing.ModSTD,
        Busbar: panelSizing.Busbar,
        FusedXL: panelSizing.FusedXL,
        S6XL: panelSizing.S6XL,
        SoftStart: panelSizing.SoftStart,
        Options: panelSizing.Options,
        Status: panelSizing.Status,
        Action: panelSizing.Action
      };
    });
  }, [
    generalProjectData,
    electricalOptionsData,
    waterTreatmentData,
    generalStationData,
    fertigationData,
    pumpsData,
    panelSizingData
  ]);

  const filteredAndSortedData = useMemo(() => {
    let filtered = combinedData.filter((row) => row.Status === 'Review' || row.Status === 'WIP');

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((row) =>
        row.JobNumber?.toLowerCase().includes(searchLower) ||
        row.ProjectName?.toLowerCase().includes(searchLower) ||
        row.ModelNumber?.toLowerCase().includes(searchLower)
      );
    }

    return [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
  }, [combinedData, searchTerm, sortConfig]);

  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = filteredAndSortedData.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortConfig.key, sortConfig.direction]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending'
    }));
  };

  const getFieldValue = (projectId, fieldName, originalValue) => {
    if (editedValues[projectId]?.[fieldName] !== undefined) {
      return editedValues[projectId][fieldName];
    }
    return originalValue || '';
  };

  const clearFieldLocalState = (projectId, fieldName, fieldKey) => {
    setEditedValues((prev) => {
      const updated = { ...prev };
      if (updated[projectId]) {
        delete updated[projectId][fieldName];
        if (Object.keys(updated[projectId]).length === 0) delete updated[projectId];
      }
      return updated;
    });

    setEditingFields((prev) => {
      const updated = { ...prev };
      delete updated[fieldKey];
      return updated;
    });
  };

  const recordChangeLog = async (projectId, jobNumber, jobName, fieldName, prevValue, newValue) => {
    if (!apiUrls.panelSizingChangeLog) return;
    try {
      await transport.post(apiUrls.panelSizingChangeLog, {
        projectID: projectId,
        currentUser,
        userRole: primaryRole,
        jobNumber: jobNumber || '',
        jobName: jobName || '',
        field: fieldName,
        prevValue: prevValue == null ? '' : String(prevValue),
        newValue: newValue == null ? '' : String(newValue),
        dateTime: new Date().toISOString()
      });
    } catch (changeLogError) {
      console.error('Error recording change log:', changeLogError);
    }
  };

  const fetchExistingRecord = projectId => findPanelRecord(dispatch, `${apiUrls.panelSizing}?projectId=${projectId}`, projectId);

  const saveField = (projectId, fieldName, value, jobNumber, jobName) => serializePanelSave(
    dispatch, JSON.stringify([apiUrls.panelSizing, userDetails?.localAccountId, projectId]), async (assertCurrent) => {
    assertCurrent();
    const panelSizingUrl = apiUrls.panelSizing;
    const isUpdatedApi = updatedApis.panelSizing;
    const existingRecord = await fetchExistingRecord(projectId);
    assertCurrent();
    const apiFieldName = fieldName === 'ModStd' ? 'ModSTD' : fieldName;

    if (existingRecord?.ID) {
      if (String(existingRecord[apiFieldName] ?? '') === String(value ?? '')) return;

      const patchUrl = isUpdatedApi
        ? `${panelSizingUrl}?projectId=${projectId}`
        : `${panelSizingUrl}/${existingRecord.ID}`;

      await transport.patch(patchUrl, {
        ID: existingRecord.ID,
        ProjectID: projectId,
        [apiFieldName]: value
      });

      await recordChangeLog(projectId, jobNumber, jobName, apiFieldName, existingRecord[apiFieldName], value);
      return;
    }

    const postUrl = isUpdatedApi ? `${panelSizingUrl}?projectID=${projectId}` : panelSizingUrl;
    const payloadField = apiFieldName.charAt(0).toLowerCase() + apiFieldName.slice(1);

    await transport.post(postUrl, {
      projectID: projectId,
      [payloadField]: value
    });

    await recordChangeLog(projectId, jobNumber, jobName, apiFieldName, '', value);
  });

  const saveMultipleFields = (projectId, fields, jobNumber, jobName) => serializePanelSave(
    dispatch, JSON.stringify([apiUrls.panelSizing, userDetails?.localAccountId, projectId]), async (assertCurrent) => {
    assertCurrent();
    const panelSizingUrl = apiUrls.panelSizing;
    const isUpdatedApi = updatedApis.panelSizing;
    const existingRecord = await fetchExistingRecord(projectId);
    assertCurrent();

    if (existingRecord?.ID) {
      const mappedFields = {};
      Object.entries(fields).forEach(([key, val]) => {
        mappedFields[key === 'ModStd' ? 'ModSTD' : key] = val;
      });

      const patchUrl = isUpdatedApi
        ? `${panelSizingUrl}?projectId=${projectId}`
        : `${panelSizingUrl}/${existingRecord.ID}`;

      await transport.patch(patchUrl, {
        ID: existingRecord.ID,
        ProjectID: projectId,
        ...mappedFields
      });

      for (const [fieldName, val] of Object.entries(mappedFields)) {
        await recordChangeLog(projectId, jobNumber, jobName, fieldName, existingRecord[fieldName], val);
      }
      return;
    }

    const postUrl = isUpdatedApi ? `${panelSizingUrl}?projectID=${projectId}` : panelSizingUrl;
    const payload = { projectID: projectId };
    Object.entries(fields).forEach(([key, val]) => {
      const apiFieldName = key === 'ModStd' ? 'ModSTD' : key;
      payload[apiFieldName.charAt(0).toLowerCase() + apiFieldName.slice(1)] = val;
    });

    await transport.post(postUrl, payload);

    for (const [fieldName, val] of Object.entries(fields)) {
      const apiFieldName = fieldName === 'ModStd' ? 'ModSTD' : fieldName;
      await recordChangeLog(projectId, jobNumber, jobName, apiFieldName, '', val);
    }
  });

  const handleFieldChange = (projectId, fieldName, value) => {
    setEditedValues((prev) => ({
      ...prev,
      [projectId]: {
        ...(prev[projectId] || {}),
        [fieldName]: value
      }
    }));
  };

  const handleFieldBlur = async (projectId, fieldName, value, row) => {
    const fieldKey = `${projectId}_${fieldName}`;
    const original = panelSizingData[projectId]?.[fieldName === 'ModStd' ? 'ModSTD' : fieldName] || '';

    if (String(original ?? '') === String(value ?? '')) {
      clearFieldLocalState(projectId, fieldName, fieldKey);
      return;
    }

    setSavingFields((prev) => ({ ...prev, [fieldKey]: true }));
    try {
      await saveField(projectId, fieldName, value, row.JobNumber, row.ProjectName);
      if (showSaveNotificationMessage) showSaveNotificationMessage('Panel sizing updated successfully', 'success');
      if (onDataUpdate) await onDataUpdate();
      clearFieldLocalState(projectId, fieldName, fieldKey);
    } catch (saveError) {
      console.error(`Error saving ${fieldName}:`, saveError);
      if (showSaveNotificationMessage) {
        showSaveNotificationMessage(
          `Failed to save ${fieldName}: ${saveError.response?.data?.message || saveError.message}`,
          'danger'
        );
      }
    } finally {
      setSavingFields((prev) => {
        const updated = { ...prev };
        delete updated[fieldKey];
        return updated;
      });
    }
  };

  const handleActionSave = async (projectId, value, row) => {
    const fieldKey = `${projectId}_Action`;
    const originalAction = panelSizingData[projectId]?.Action || '';

    if (String(originalAction ?? '') === String(value ?? '')) {
      clearFieldLocalState(projectId, 'Action', fieldKey);
      return;
    }

    setSavingFields((prev) => ({ ...prev, [fieldKey]: true }));
    try {
      if (value && row.Status === 'Review') {
        await saveMultipleFields(projectId, { Action: value, Status: 'WIP' }, row.JobNumber, row.ProjectName);
      } else {
        await saveField(projectId, 'Action', value, row.JobNumber, row.ProjectName);
      }
      if (showSaveNotificationMessage) showSaveNotificationMessage('Panel sizing updated successfully', 'success');
      if (onDataUpdate) await onDataUpdate();
      clearFieldLocalState(projectId, 'Action', fieldKey);
    } catch (saveError) {
      console.error('Error saving Action:', saveError);
      if (showSaveNotificationMessage) {
        showSaveNotificationMessage(
          `Failed to save Action: ${saveError.response?.data?.message || saveError.message}`,
          'danger'
        );
      }
    } finally {
      setSavingFields((prev) => {
        const updated = { ...prev };
        delete updated[fieldKey];
        return updated;
      });
    }
  };

  const handleMarkAsDone = async (row) => {
    const projectId = row.ID;
    setMarkingDone((prev) => ({ ...prev, [projectId]: true }));

    try {
      const currentAction = getFieldValue(projectId, 'Action', row.Action);
      if (row.Status === 'Review' && !currentAction) {
        await saveMultipleFields(projectId, { Action: 'No Action', Status: 'Done' }, row.JobNumber, row.ProjectName);
      } else {
        await saveField(projectId, 'Status', 'Done', row.JobNumber, row.ProjectName);
      }
      if (showSaveNotificationMessage) showSaveNotificationMessage('Marked as done', 'success');
      if (onDataUpdate) await onDataUpdate();
    } catch (markError) {
      console.error('Error marking as done:', markError);
      if (showSaveNotificationMessage) {
        showSaveNotificationMessage(
          `Failed to mark as done: ${markError.response?.data?.message || markError.message}`,
          'danger'
        );
      }
    } finally {
      setMarkingDone((prev) => {
        const updated = { ...prev };
        delete updated[projectId];
        return updated;
      });
    }
  };

  const renderEditableStatusField = (row) => {
    const fieldName = 'Status';
    const fieldKey = `${row.ID}_${fieldName}`;
    const isSaving = savingFields[fieldKey];
    const isEditing = editingFields[fieldKey];
    const currentValue = getFieldValue(row.ID, fieldName, row.Status);

    if (isEditing) {
      return (
        <Form.Select
          size="sm"
          value={currentValue}
          onChange={(e) => {
            const newValue = e.target.value;
            handleFieldChange(row.ID, fieldName, newValue);
            handleFieldBlur(row.ID, fieldName, newValue, row);
          }}
          disabled={isSaving}
          autoFocus
          style={{ minWidth: '120px', fontSize: '0.875rem', backgroundColor: isSaving ? '#f8f9fa' : 'white' }}
        >
          <option value="">Select...</option>
          {statusOptions.map((option) => (
            <option key={option.ID} value={option.DisplayText || option.displayName}>
              {option.DisplayText || option.displayName}
            </option>
          ))}
        </Form.Select>
      );
    }

    const isValid = !currentValue || isValueInDropdown(currentValue, statusOptions);
    return (
      <div
        onDoubleClick={() => setEditingFields((prev) => ({ ...prev, [fieldKey]: true }))}
        style={{
          cursor: 'pointer',
          padding: '4px 8px',
          minHeight: '24px',
          borderRadius: '4px',
          transition: 'background-color 0.2s',
          color: isValid ? 'inherit' : '#dc3545',
          fontWeight: isValid ? 'normal' : 'bold'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
  title={isValid ? 'Double-click to edit' : `Invalid value: ${currentValue}`}
      >
        {currentValue || '-'}
      </div>
    );
  };

  const renderEditableActionField = (row) => {
    const fieldName = 'Action';
    const fieldKey = `${row.ID}_${fieldName}`;
    const isSaving = savingFields[fieldKey];
    const isEditing = editingFields[fieldKey];
    const currentValue = getFieldValue(row.ID, fieldName, row.Action);

    if (isEditing) {
      return (
        <Form.Select
          size="sm"
          value={currentValue}
          onChange={(e) => {
            const newValue = e.target.value;
            handleFieldChange(row.ID, fieldName, newValue);
            handleActionSave(row.ID, newValue, row);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') clearFieldLocalState(row.ID, fieldName, fieldKey);
          }}
          disabled={isSaving}
          autoFocus
          style={{ minWidth: '160px', fontSize: '0.875rem', backgroundColor: isSaving ? '#f8f9fa' : 'white' }}
        >
          <option value="">Select...</option>
          {actionOptions.map((option) => (
            <option key={option.ID} value={option.DisplayText || option.displayName}>
              {option.DisplayText || option.displayName}
            </option>
          ))}
          {!isValueInDropdown(currentValue, actionOptions) && currentValue && (
            <option value={currentValue}>{currentValue}</option>
          )}
        </Form.Select>
      );
    }

    const isValid = !currentValue || actionOptions.length === 0 || isValueInDropdown(currentValue, actionOptions);
    return (
      <div
        onDoubleClick={() => setEditingFields((prev) => ({ ...prev, [fieldKey]: true }))}
        style={{
          cursor: 'pointer',
          padding: '4px 8px',
          minHeight: '24px',
          borderRadius: '4px',
          transition: 'background-color 0.2s',
          color: isValid ? 'inherit' : '#dc3545',
          fontWeight: isValid ? 'normal' : 'bold'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        title={isValid ? 'Double-click to edit' : `Invalid value: ${currentValue}`}
      >
        {currentValue || '-'}
      </div>
    );
  };

  const renderEditableField = (row, fieldName, value) => {
    const fieldKey = `${row.ID}_${fieldName}`;
    const isSaving = savingFields[fieldKey];
    const isEditing = editingFields[fieldKey];
    const currentValue = getFieldValue(row.ID, fieldName, value);

    if (isEditing) {
      return (
        <Form.Control
          type="text"
          size="sm"
          value={currentValue}
          onChange={(e) => handleFieldChange(row.ID, fieldName, e.target.value)}
          onBlur={(e) => handleFieldBlur(row.ID, fieldName, e.target.value, row)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.target.blur();
            if (e.key === 'Escape') clearFieldLocalState(row.ID, fieldName, fieldKey);
          }}
          disabled={isSaving}
          autoFocus
          style={{ minWidth: '80px', fontSize: '0.875rem', backgroundColor: isSaving ? '#f8f9fa' : 'white' }}
        />
      );
    }

    return (
      <div
        onDoubleClick={() => setEditingFields((prev) => ({ ...prev, [fieldKey]: true }))}
        style={{ cursor: 'pointer', padding: '4px 8px', minHeight: '24px', borderRadius: '4px', transition: 'background-color 0.2s' }}
        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
        title="Double-click to edit"
      >
        {currentValue || '-'}
      </div>
    );
  };

  const isValidJobNumber = (jobNumber) => {
    if (!jobNumber || jobNumber.length < 4) return false;
    const year = jobNumber.substring(0, 4);
    return year.startsWith('20') && !isNaN(year);
  };

  const generateFilePath = (jobNumber) => {
    if (!isValidJobNumber(jobNumber)) return null;
    const year = jobNumber.substring(0, 4);
    return `\\\\h2ofs01\\Engineering\\ELECTRICAL\\WT_ACADE\\PROJECTS\\JOBS\\${year}\\${jobNumber}`;
  };

  const generateFolderPath = (jobNumber) => {
    if (!isValidJobNumber(jobNumber)) return null;
    const year = jobNumber.substring(0, 4);
    const yearShort = year.substring(2, 4);
    return `\\\\h2ofs01\\jobs\\${yearShort}_Jobs\\${jobNumber}\\ELECTRICAL\\SCHEMATICS-BOM-IO\\PANEL`;
  };

  const handleFilePathClick = (e, jobNumber) => {
    e.stopPropagation();
    const path = generateFilePath(jobNumber);
    if (!path) return;
    const opened = window.open(`file://${path.replace(/\\\\/g, '/')}`, '_blank');
    if (!opened || opened.closed || typeof opened.closed === 'undefined') {
      navigator.clipboard.writeText(path).then(() => {
        setCopiedJobId(jobNumber);
        setTimeout(() => setCopiedJobId(null), 2000);
      }).catch((copyError) => {
        console.error('Failed to copy path:', copyError);
      });
    }
  };

  const handleFolderPathClick = (e, jobNumber) => {
    e.stopPropagation();
    const path = generateFolderPath(jobNumber);
    if (!path) return;
    const opened = window.open(`file://${path.replace(/\\\\/g, '/')}`, '_blank');
    if (!opened || opened.closed || typeof opened.closed === 'undefined') {
      navigator.clipboard.writeText(path).then(() => {
        setCopiedFolderJobId(jobNumber);
        setTimeout(() => setCopiedFolderJobId(null), 2000);
      }).catch((copyError) => {
        console.error('Failed to copy path:', copyError);
      });
    }
  };

  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="bi bi-chevron-expand ms-1" style={{ fontSize: '0.8rem', color: '#999' }}></i>;
    }
    return sortConfig.direction === 'ascending'
      ? <i className="bi bi-chevron-up ms-1" style={{ fontSize: '0.8rem' }}></i>
      : <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.8rem' }}></i>;
  };

  if (loading && combinedData.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error Loading Data</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <div className="log-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>WIP — Electrical Job Log</h3>
        <div className="d-flex gap-2 align-items-center">
          <span className="text-muted">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredAndSortedData.length)} of {filteredAndSortedData.length} jobs
          </span>
          <Form.Control
            type="text"
            placeholder="Search by Job, Description, or Model..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '300px' }}
          />
        </div>
      </div>

      <div className="table-responsive">
        <Table striped bordered hover size="sm" className="log-table">
          <thead>
            <tr>
              <th>L</th>
              <th>M</th>
              <th onClick={() => handleSort('Status')} style={{ cursor: 'pointer' }}>STATUS {renderSortIcon('Status')}</th>
              <th>ACTION</th>
              <th onClick={() => handleSort('JobNumber')} style={{ cursor: 'pointer' }}>JOB {renderSortIcon('JobNumber')}</th>
              <th onClick={() => handleSort('ProjectName')} style={{ cursor: 'pointer' }}>DESCRIPTION {renderSortIcon('ProjectName')}</th>
              <th onClick={() => handleSort('ModelNumber')} style={{ cursor: 'pointer' }}>MODEL {renderSortIcon('ModelNumber')}</th>
              <th onClick={() => handleSort('ElectricalEnclosureActualSize')} style={{ cursor: 'pointer' }}>SIZE {renderSortIcon('ElectricalEnclosureActualSize')}</th>
              <th>ORIENTATION</th>
              <th>DRIVES</th>
              <th>COOLING</th>
              <th>HMI</th>
              <th onClick={() => handleSort('Sunshade')} style={{ cursor: 'pointer' }}>SUNSHADE {renderSortIcon('Sunshade')}</th>
              <th>DOOR MODS</th>
              <th onClick={() => handleSort('DeadFront')} style={{ cursor: 'pointer' }}>DEAD FRONT {renderSortIcon('DeadFront')}</th>
              <th onClick={() => handleSort('ElectricalOptions')} style={{ cursor: 'pointer' }}>JBOX {renderSortIcon('ElectricalOptions')}</th>
              <th onClick={() => handleSort('HasUV')} style={{ cursor: 'pointer' }}>UV PANEL {renderSortIcon('HasUV')}</th>
              <th onClick={() => handleSort('PrimeSystem')} style={{ cursor: 'pointer' }}>VAC PRIME {renderSortIcon('PrimeSystem')}</th>
              <th onClick={() => handleSort('WatertronicsFertigation')} style={{ cursor: 'pointer' }}>FERTIGATION {renderSortIcon('WatertronicsFertigation')}</th>
              <th onClick={() => handleSort('Zones')} style={{ cursor: 'pointer' }}>ZONE {renderSortIcon('Zones')}</th>
              <th>MOD STD</th>
              <th>BUSBAR</th>
              <th>FUSED XL</th>
              <th>S6 XL</th>
              <th>SOFT START</th>
              <th onClick={() => handleSort('IndividualMotorDisconnects')} style={{ cursor: 'pointer' }}>MOTOR DISCONNECTS {renderSortIcon('IndividualMotorDisconnects')}</th>
              <th>TR OR WF</th>
              <th>OPTIONS</th>
              <th>JOB - DESCRIPTION (MODEL)</th>
              <th>COMPLETE</th>
            </tr>
          </thead>
          <tbody>
            {currentPageData.length === 0 ? (
              <tr>
                <td colSpan="31" className="text-center text-muted py-4">
                  {searchTerm ? 'No jobs match your search criteria' : 'No jobs to display'}
                </td>
              </tr>
            ) : (
              currentPageData.map((row) => (
                <tr key={row.ID}>
                  <td className="text-center">
                    {isValidJobNumber(row.JobNumber) ? (
                      <div
                        style={{ cursor: 'pointer', position: 'relative' }}
                        onClick={(e) => handleFilePathClick(e, row.JobNumber)}
                        title={generateFilePath(row.JobNumber)}
                      >
                        <i className="bi bi-file-earmark" style={{ fontSize: '1.2rem', color: copiedJobId === row.JobNumber ? '#198754' : '#0d6efd' }}></i>
                        {copiedJobId === row.JobNumber && (
                          <small style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', color: '#198754', fontSize: '0.7rem', fontWeight: 'bold' }}>
                            Copied!
                          </small>
                        )}
                      </div>
                    ) : <span style={{ color: '#999' }}>-</span>}
                  </td>
                  <td className="text-center">
                    {isValidJobNumber(row.JobNumber) ? (
                      <div
                        style={{ cursor: 'pointer', position: 'relative' }}
                        onClick={(e) => handleFolderPathClick(e, row.JobNumber)}
                        title={generateFolderPath(row.JobNumber)}
                      >
                        <i className="bi bi-folder" style={{ fontSize: '1.2rem', color: copiedFolderJobId === row.JobNumber ? '#198754' : '#ffc107' }}></i>
                        {copiedFolderJobId === row.JobNumber && (
                          <small style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap', color: '#198754', fontSize: '0.7rem', fontWeight: 'bold' }}>
                            Copied!
                          </small>
                        )}
                      </div>
                    ) : <span style={{ color: '#999' }}>-</span>}
                  </td>
                  <td>{renderEditableStatusField(row)}</td>
                  <td style={{ minWidth: '160px' }}>{renderEditableActionField(row)}</td>
                  <td>{row.JobNumber || '-'}</td>
                  <td>{row.ProjectName || '-'}</td>
                  <td>{row.ModelNumber || '-'}</td>
                  <td>{row.ElectricalEnclosureActualSize || '-'}</td>
                  <td>{renderEditableField(row, 'Orientation', row.Orientation)}</td>
                  <td>{renderEditableField(row, 'Drives', row.Drives)}</td>
                  <td>{renderEditableField(row, 'Cooling', row.Cooling)}</td>
                  <td>{renderEditableField(row, 'HMI', row.HMI)}</td>
                  <td>{row.Sunshade || '-'}</td>
                  <td>{renderEditableField(row, 'DoorMods', row.DoorMods)}</td>
                  <td>{row.DeadFront || '-'}</td>
                  <td>{row.ElectricalOptions || '-'}</td>
                  <td>{row.HasUV ? 'Yes' : 'No'}</td>
                  <td>{row.PrimeSystem || '-'}</td>
                  <td>{row.WatertronicsFertigation || '-'}</td>
                  <td>{row.Zones || '-'}</td>
                  <td>{renderEditableField(row, 'ModStd', row.ModStd)}</td>
                  <td>{renderEditableField(row, 'Busbar', row.Busbar)}</td>
                  <td>{renderEditableField(row, 'FusedXL', row.FusedXL)}</td>
                  <td>{renderEditableField(row, 'S6XL', row.S6XL)}</td>
                  <td>{renderEditableField(row, 'SoftStart', row.SoftStart)}</td>
                  <td>{row.IndividualMotorDisconnects || '-'}</td>
                  <td>{row.HasTransfer && row.HasWaterFeature ? 'Both' : row.HasTransfer ? 'Transfer' : row.HasWaterFeature ? 'Water Feature' : '-'}</td>
                  <td style={{ minWidth: '200px' }}>{renderEditableField(row, 'Options', row.Options)}</td>
                  <td>{row.JobNumber && row.ProjectName && row.ModelNumber ? `${row.JobNumber} - ${row.ProjectName} (${row.ModelNumber})` : '-'}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="success"
                      onClick={() => handleMarkAsDone(row)}
                      disabled={markingDone[row.ID] || savingFields[`${row.ID}_Action`]}
                    >
                      {markingDone[row.ID] ? 'Saving…' : 'mark as done'}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center mt-3 gap-2">
          <Button variant="outline-primary" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</Button>
          <Button variant="outline-primary" size="sm" onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))} disabled={currentPage === 1}>Previous</Button>
          <span className="mx-3">Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
          <Button variant="outline-primary" size="sm" onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>Next</Button>
          <Button variant="outline-primary" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</Button>
        </div>
      )}

      <style jsx>{`
        .log-container {
          padding: 20px;
          background-color: #fff;
          border-radius: 0.375rem;
          box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
        }
        .log-table {
          font-size: 0.875rem;
          white-space: nowrap;
        }
        .log-table th {
          background-color: #f8f9fa;
          font-weight: 600;
          vertical-align: middle;
          padding: 8px;
          border: 1px solid #dee2e6;
        }
        .log-table td {
          vertical-align: middle;
          padding: 6px 8px;
          border: 1px solid #dee2e6;
        }
        .log-table tbody tr:hover {
          background-color: #f1f3f5;
        }
        .table-responsive {
          overflow-x: auto;
          max-width: 100%;
        }
      `}</style>
    </div>
  );
}

export default PanelSizingWIP;
